<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import { tick, untrack } from 'svelte';
  import { toast } from 'svelte-sonner';
  import { jsonHeaders, readApiErrorMessage } from '../../lib/apiHelpers';
  import type {
    Project,
    ProjectCategory,
    ProjectStatus,
    ProjectSchedulingState,
    Task,
    TaskPriority,
  } from '../../lib/types';
  import { categoryColors, categoryIcons, getCurrentWeekKey } from '../../lib/utils';
  import {
    formatProjectDate,
    formatProjectDateShort,
    formatProjectWeekSpan,
  } from '../../lib/projectDateRange';
  import { computePosition, flip, shift, offset } from '@floating-ui/dom';
  import { subscribeToCachedTasks, fetchProjectTasks } from '../../lib/stores/projectTasksCache';
  import { completeTask } from '../../lib/taskApi';
  import {
    loadCollapsedLanes,
    persistCollapsedLanes,
    resolveLaneMove,
    scheduledDatesForWeek,
    toggleCollapsedLane,
    PROJECT_LANES,
    type ProjectLane,
  } from '../../lib/projectLanesUtils';
  import {
    toggleFilterValue,
    sortProjects,
    groupProjects,
    formatMonthKey,
    STATUS_ORDER,
    STATUS_GROUP_ORDER,
    STATUS_GROUP_LABELS,
    type SortColumn,
    type SortDirection,
    type GroupBy,
    type ProjectGroup,
  } from '../../lib/projectIndexUtils';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import ProjectModal from '../../components/ProjectModal.svelte';
  import Icon from '../../components/Icon.svelte';
  import ProjectRail from '../../components/ProjectRail.svelte';
  import ProjectLanesView from '../../components/ProjectLanesView.svelte';
  import LaneColumnsOptions from '../../components/LaneColumnsOptions.svelte';
  import { openProjectModalForCreate, openProjectScheduleModal } from '../../lib/stores/ui';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import NavTooltip from '../../components/NavTooltip.svelte';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import OptionSheet from '../../components/mobile/OptionSheet.svelte';
  import { scrollFade } from '../../lib/scrollFade';
  import { viewport } from '../../lib/stores/viewport';
  import { undoableAction, undoableSoftDelete } from '../../lib/undoManager';

  interface Props {
    projects: Project[];
    filters: {
      statuses: string[];
      categories: string[];
      schedulingStates: string[];
    };
  }

  let { projects, filters }: Props = $props();

  // Local projects state for optimistic inline editing updates
  // svelte-ignore state_referenced_locally
  let localProjects = $state<Project[]>(projects.map((p) => ({ ...p })));
  // Plain variable (NOT $state) to track prop identity — $state would wrap in a
  // different proxy, making the reference comparison always fail and causing an
  // infinite effect loop (effect_update_depth_exceeded).
  // svelte-ignore state_referenced_locally
  let syncedPropsRef = projects;

  // Re-sync from Inertia props when they change (e.g., after ProjectModal creates a project)
  // eslint-disable-next-line momentium/no-unguarded-effect -- sync external Inertia props into local state on navigation
  $effect(() => {
    const newProjects = projects;
    if (newProjects !== syncedPropsRef) {
      localProjects = newProjects.map((p) => ({ ...p }));
      syncedPropsRef = newProjects;
    }
  });

  function updateLocalProject(projectId: string, updates: Partial<Project>) {
    localProjects = localProjects.map((p) => (p.id === projectId ? { ...p, ...updates } : p));
  }

  // Filter state (multi-select with Sets)
  let searchQuery = $state('');
  let filterStatuses = $state<Set<string>>(new Set());
  let filterCategories = $state<Set<string>>(new Set());
  let filterPriority = $state(false);

  // Filter dropdown state
  let openFilter = $state<'category' | 'status' | null>(null);
  let categoryBtnEl: HTMLButtonElement | undefined = $state();
  let statusBtnEl: HTMLButtonElement | undefined = $state();
  let categoryPopoverEl: HTMLDivElement | undefined = $state();
  let statusPopoverEl: HTMLDivElement | undefined = $state();
  let categoryPopoverStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let statusPopoverStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  async function positionFilterPopover(
    btnEl: HTMLElement,
    popoverEl: HTMLDivElement,
    setter: (style: string) => void,
  ) {
    const result = await computePosition(btnEl, popoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
        shift({ padding: 8 }),
      ],
    });
    setter(`position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`);
  }

  function toggleFilter(which: 'category' | 'status') {
    if (openFilter === which) {
      openFilter = null;
    } else {
      openFilter = which;
    }
  }

  // Position popovers when they open
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI positioning: must compute popover position when filter opens, depends on DOM element refs
  $effect(() => {
    if (openFilter === 'category' && categoryBtnEl && categoryPopoverEl) {
      positionFilterPopover(categoryBtnEl, categoryPopoverEl, (s) => (categoryPopoverStyle = s));
    }
    if (openFilter === 'status' && statusBtnEl && statusPopoverEl) {
      positionFilterPopover(statusBtnEl, statusPopoverEl, (s) => (statusPopoverStyle = s));
    }
  });

  function clearAllFilters() {
    filterStatuses = new Set();
    filterCategories = new Set();
    filterPriority = false;
  }

  // Close popovers on outside click
  function handlePageClick(e: MouseEvent) {
    const target = e.target as HTMLElement;

    // Close filter popovers
    if (openFilter !== null) {
      if (openFilter === 'category') {
        if (!categoryBtnEl?.contains(target) && !categoryPopoverEl?.contains(target)) {
          openFilter = null;
        }
      } else if (openFilter === 'status') {
        if (!statusBtnEl?.contains(target) && !statusPopoverEl?.contains(target)) {
          openFilter = null;
        }
      }
    }

    // Close inline status picker
    if (inlineStatusPickerProjectId !== null) {
      if (
        !inlineStatusPickerPopoverEl?.contains(target) &&
        !inlineStatusPickerBtnEl?.contains(target)
      ) {
        inlineStatusPickerProjectId = null;
      }
    }

    // Close inline category picker
    if (inlineCategoryPickerProjectId !== null) {
      if (
        !inlineCategoryPickerPopoverEl?.contains(target) &&
        !inlineCategoryPickerBtnEl?.contains(target)
      ) {
        inlineCategoryPickerProjectId = null;
      }
    }

    // Close row actions menu
    if (rowActionsProjectId !== null) {
      if (!rowActionsPopoverEl?.contains(target) && !rowActionsBtnEl?.contains(target)) {
        rowActionsProjectId = null;
      }
    }
  }

  function handleFilterKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (openFilter !== null) openFilter = null;
      if (editingTitleProjectId !== null) cancelEditTitle();
      if (inlineStatusPickerProjectId !== null) inlineStatusPickerProjectId = null;
      if (inlineCategoryPickerProjectId !== null) inlineCategoryPickerProjectId = null;
      if (rowActionsProjectId !== null) {
        rowActionsProjectId = null;
        rowActionsBtnEl?.focus();
      }
    }
  }

  // Category button label
  let categoryBtnLabel = $derived(
    filterCategories.size === 0 ? 'All categories' : `Category (${filterCategories.size})`,
  );

  // Status button label
  let statusBtnLabel = $derived(
    filterStatuses.size === 0 ? 'All statuses' : `Status (${filterStatuses.size})`,
  );

  // Filtered projects
  let filteredProjects = $derived.by(() => {
    let result = localProjects;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.prefix?.toLowerCase().includes(q) ||
          p.notes?.toLowerCase().includes(q),
      );
    }

    if (filterStatuses.size > 0) {
      result = result.filter((p) => filterStatuses.has(p.status));
    }

    if (filterCategories.size > 0) {
      result = result.filter((p) => filterCategories.has(p.category));
    }

    return result;
  });

  // Grouping
  let archivedProjects = $derived(filteredProjects.filter((p) => p.isArchived));
  let activeProjects = $derived(
    filteredProjects.filter(
      (p) => !p.isArchived && p.status !== 'done' && p.schedulingState === 'scheduled',
    ),
  );
  let backlogProjects = $derived(
    filteredProjects.filter((p) => !p.isArchived && p.schedulingState === 'backlog'),
  );
  let doneProjects = $derived(filteredProjects.filter((p) => !p.isArchived && p.status === 'done'));

  // Tab state
  type Tab = 'scheduled' | 'backlog' | 'done' | 'archived' | 'all';
  let activeTab = $state<Tab>('scheduled');

  let displayedProjects = $derived.by(() => {
    switch (activeTab) {
      case 'scheduled':
        return activeProjects;
      case 'backlog':
        return backlogProjects;
      case 'done':
        return doneProjects;
      case 'archived':
        return archivedProjects;
      case 'all':
        return filteredProjects;
    }
  });

  // View mode state (persisted to localStorage)
  type ViewMode = 'table' | 'grid' | 'lanes';
  function loadPersistedViewMode(): ViewMode {
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('projectIndex:viewMode');
        if (raw === 'table' || raw === 'grid' || raw === 'lanes') return raw;
      } catch {
        /* use default */
      }
    }
    return 'table';
  }

  let viewMode = $state<ViewMode>(loadPersistedViewMode());
  let collapsedLanes = $state<Set<ProjectLane>>(
    loadCollapsedLanes(typeof localStorage !== 'undefined' ? localStorage : null),
  );

  function persistLaneCollapse(next: Set<ProjectLane>) {
    collapsedLanes = next;
    persistCollapsedLanes(next, typeof localStorage !== 'undefined' ? localStorage : null);
  }

  function toggleLaneCollapse(lane: ProjectLane) {
    persistLaneCollapse(toggleCollapsedLane(collapsedLanes, lane));
  }

  function showAllLanes() {
    persistLaneCollapse(new Set());
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- persist viewMode preference to localStorage on change
  $effect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('projectIndex:viewMode', viewMode);
    }
  });

  // A user's selected presentation applies at every viewport. Each presentation
  // supplies its own compact layout rather than forcing phones into grid cards.
  let effectiveViewMode = $derived<ViewMode>(viewMode);
  let activeCompactLane = $state<ProjectLane>('backlog');
  let compactDropTarget = $state<ProjectLane | null>(null);
  let compactLanes = $derived($viewport.width <= 1024);
  let hasActiveFilters = $derived(
    filterCategories.size > 0 ||
      (effectiveViewMode === 'lanes' ? filterPriority : filterStatuses.size > 0),
  );

  // Mobile filter collapse
  let showMobileFilters = $state(false);

  // Grid card expand state and task data
  let expandedCards = $state<Set<string>>(new Set());

  function toggleCardExpand(projectId: string) {
    const next = new Set(expandedCards);
    if (next.has(projectId)) {
      next.delete(projectId);
    } else {
      next.add(projectId);
    }
    expandedCards = next;
  }

  // Task data for expanded cards — managed via $effect subscriptions in the template
  // We store per-project task state here for the grid cards
  let cardTaskData = $state<Record<string, { tasks: Task[]; loading: boolean; fetched: boolean }>>(
    {},
  );

  // Subscribe to task cache for expanded cards
  // eslint-disable-next-line momentium/no-unguarded-effect -- subscribes to task cache for expanded cards, manages cleanup
  $effect(() => {
    const expanded = expandedCards;
    const unsubs: (() => void)[] = [];

    for (const projectId of expanded) {
      const unsub = subscribeToCachedTasks(projectId, (data) => {
        // Use untrack to avoid reading cardTaskData as a dependency —
        // subscribeToCachedTasks calls this callback synchronously on subscribe
        // (Svelte store behavior), so without untrack the read+write of cardTaskData
        // during the effect's tracking context causes an infinite loop.
        const current = untrack(() => cardTaskData);
        cardTaskData = { ...current, [projectId]: data };
      });
      unsubs.push(unsub);
      // Trigger fetch if not yet fetched
      fetchProjectTasks(projectId);
    }

    return () => {
      unsubs.forEach((fn) => fn());
    };
  });

  async function toggleCardTask(projectId: string, taskId: string) {
    const data = cardTaskData[projectId];
    if (!data) return;
    const result = await completeTask(projectId, taskId, data.tasks);
    if (result.success) {
      cardTaskData = { ...cardTaskData, [projectId]: { ...data, tasks: result.tasks } };
    }
  }

  // Sort state (persisted to localStorage)
  function loadPersistedSort(): { column: SortColumn; direction: SortDirection } {
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('projectIndex:sort');
        if (raw) return JSON.parse(raw);
      } catch {
        /* use default */
      }
    }
    return { column: 'targetDate', direction: 'desc' };
  }

  const initialSort = loadPersistedSort();
  let sortColumn = $state<SortColumn>(initialSort.column);
  let sortDirection = $state<SortDirection>(initialSort.direction);

  // Lanes columns already encode status — ignore the table status filter here.
  let lanesProjects = $derived.by(() => {
    let result = localProjects.filter((p) => !p.isArchived);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.prefix?.toLowerCase().includes(q) ||
          p.notes?.toLowerCase().includes(q),
      );
    }
    if (filterCategories.size > 0) {
      result = result.filter((p) => filterCategories.has(p.category));
    }
    if (filterPriority) {
      result = result.filter((p) => p.isPriority);
    }
    return sortProjects(result, sortColumn, sortDirection);
  });

  function handleSort(column: SortColumn) {
    if (sortColumn === column) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortColumn = column;
      sortDirection = 'asc';
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        'projectIndex:sort',
        JSON.stringify({ column: sortColumn, direction: sortDirection }),
      );
    }
  }

  function resetSort() {
    sortColumn = 'targetDate';
    sortDirection = 'desc';
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        'projectIndex:sort',
        JSON.stringify({ column: 'targetDate', direction: 'desc' }),
      );
    }
  }

  function ariaSortFor(column: SortColumn): 'ascending' | 'descending' | 'none' {
    if (sortColumn !== column) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  const sortColumnLabels: Record<SortColumn, string> = {
    title: 'Title',
    status: 'Status',
    category: 'Category',
    targetDate: 'Date',
    tasks: 'Tasks',
  };

  let sortColumnLabel = $derived(sortColumnLabels[sortColumn]);

  // Phones: the view switch and sort live in bottom sheets (ma-p6zx), so only the
  // status tab row is left in the toolbar and the table has no sortable header.
  let viewSheetOpen = $state(false);
  let sortSheetOpen = $state(false);
  const VIEW_OPTIONS: { value: ViewMode; label: string; icon: string }[] = [
    { value: 'table', label: 'Table', icon: '≡' },
    { value: 'grid', label: 'Grid', icon: '⊞' },
    { value: 'lanes', label: 'Lanes', icon: '▥' },
  ];
  let viewIcon = $derived(VIEW_OPTIONS.find((o) => o.value === viewMode)?.icon ?? '≡');
  let sortSheetOptions = $derived(
    (Object.keys(sortColumnLabels) as SortColumn[]).map((column) => ({
      value: column,
      label:
        sortColumn === column
          ? `${sortColumnLabels[column]} ${sortDirection === 'asc' ? '↑' : '↓'}`
          : sortColumnLabels[column],
    })),
  );
  let isDefaultSort = $derived(sortColumn === 'targetDate' && sortDirection === 'desc');

  let sortedProjects = $derived(sortProjects(displayedProjects, sortColumn, sortDirection));

  const statusIcons: Record<string, string> = {
    planned: '📋',
    active: '🔄',
    done: '✅',
  };

  // === Inline Editing State ===

  const statusOptions: { value: ProjectStatus; icon: string; label: string }[] = [
    { value: 'planned', icon: '📋', label: 'Planned' },
    { value: 'active', icon: '🔄', label: 'Active' },
    { value: 'done', icon: '✅', label: 'Done' },
  ];

  const categoryOptions: { value: ProjectCategory; icon: string; label: string }[] = [
    { value: 'tech', icon: '💻', label: 'Tech' },
    { value: 'health', icon: '🏃', label: 'Health' },
    { value: 'personal', icon: '👤', label: 'Personal' },
    { value: 'finance', icon: '💰', label: 'Finance' },
    { value: 'travel', icon: '✈️', label: 'Travel' },
    { value: 'other', icon: '📋', label: 'Other' },
  ];

  // Title editing
  let editingTitleProjectId = $state<string | null>(null);
  let editingTitleValue = $state('');
  let titleInputEl: HTMLInputElement | undefined = $state();

  // Inline status picker
  let inlineStatusPickerProjectId = $state<string | null>(null);
  let inlineStatusPickerBtnEl: HTMLElement | undefined = $state();
  let inlineStatusPickerPopoverEl: HTMLDivElement | undefined = $state();
  let inlineStatusPickerStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  // Inline category picker
  let inlineCategoryPickerProjectId = $state<string | null>(null);
  let inlineCategoryPickerBtnEl: HTMLElement | undefined = $state();
  let inlineCategoryPickerPopoverEl: HTMLDivElement | undefined = $state();
  let inlineCategoryPickerStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  // Row actions menu + delete confirm
  let rowActionsProjectId = $state<string | null>(null);
  let rowActionsBtnEl: HTMLElement | undefined = $state();
  let rowActionsPopoverEl: HTMLDivElement | undefined = $state();
  let rowActionsStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let rowActionsFirstItemEl: HTMLButtonElement | undefined = $state();

  function closeAllInlineEdits() {
    editingTitleProjectId = null;
    inlineStatusPickerProjectId = null;
    inlineCategoryPickerProjectId = null;
  }

  // Title editing functions
  function startEditTitle(projectId: string, currentTitle: string) {
    closeAllInlineEdits();
    openFilter = null;
    editingTitleProjectId = projectId;
    editingTitleValue = currentTitle;
    tick().then(() => titleInputEl?.focus());
  }

  async function saveInlineTitle(projectId: string) {
    if (cancellingEdit) return;
    const newTitle = editingTitleValue.trim();
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj || !newTitle || newTitle === proj.title) {
      editingTitleProjectId = null;
      return;
    }
    const prev = proj.title;
    updateLocalProject(projectId, { title: newTitle });
    editingTitleProjectId = null;

    try {
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ project: { title: newTitle } }),
      });
      if (!response.ok) {
        updateLocalProject(projectId, { title: prev });
        toast.error(await readApiErrorMessage(response, 'Failed to update title'));
      }
    } catch {
      updateLocalProject(projectId, { title: prev });
      toast.error('Failed to update title');
    }
  }

  let cancellingEdit = false;

  function cancelEditTitle() {
    cancellingEdit = true;
    editingTitleProjectId = null;
    // Reset flag after the blur handler has had a chance to check it
    tick().then(() => {
      cancellingEdit = false;
    });
  }

  // Status picker functions
  function openInlineStatusPicker(projectId: string, btnEl: HTMLElement) {
    closeAllInlineEdits();
    openFilter = null;
    inlineStatusPickerProjectId = projectId;
    inlineStatusPickerBtnEl = btnEl;
    tick().then(positionInlineStatusPicker);
  }

  async function positionInlineStatusPicker() {
    if (!inlineStatusPickerBtnEl || !inlineStatusPickerPopoverEl) return;
    const result = await computePosition(inlineStatusPickerBtnEl, inlineStatusPickerPopoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    inlineStatusPickerStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  async function setInlineStatus(projectId: string, newStatus: ProjectStatus) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj || proj.status === newStatus) {
      inlineStatusPickerProjectId = null;
      return;
    }
    const prev = proj.status;
    updateLocalProject(projectId, { status: newStatus });
    inlineStatusPickerProjectId = null;

    try {
      const response = await fetch(`/projects/${projectId}/update_status`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) {
        updateLocalProject(projectId, { status: prev });
        toast.error('Failed to update status');
      }
    } catch {
      updateLocalProject(projectId, { status: prev });
      toast.error('Failed to update status');
    }
  }

  // Category picker functions
  function openInlineCategoryPicker(projectId: string, btnEl: HTMLElement) {
    closeAllInlineEdits();
    openFilter = null;
    inlineCategoryPickerProjectId = projectId;
    inlineCategoryPickerBtnEl = btnEl;
    tick().then(positionInlineCategoryPicker);
  }

  async function positionInlineCategoryPicker() {
    if (!inlineCategoryPickerBtnEl || !inlineCategoryPickerPopoverEl) return;
    const result = await computePosition(inlineCategoryPickerBtnEl, inlineCategoryPickerPopoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    inlineCategoryPickerStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  async function setInlineCategory(projectId: string, newCategory: ProjectCategory) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj || proj.category === newCategory) {
      inlineCategoryPickerProjectId = null;
      return;
    }
    const prev = proj.category;
    updateLocalProject(projectId, { category: newCategory });
    inlineCategoryPickerProjectId = null;

    try {
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ project: { category: newCategory } }),
      });
      if (!response.ok) {
        updateLocalProject(projectId, { category: prev });
        toast.error('Failed to update category');
      }
    } catch {
      updateLocalProject(projectId, { category: prev });
      toast.error('Failed to update category');
    }
  }

  // Priority toggle
  async function toggleInlinePriority(projectId: string) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj) return;
    closeAllInlineEdits();
    const prev = proj.isPriority;
    updateLocalProject(projectId, { isPriority: !prev });

    try {
      const response = await fetch(`/projects/${projectId}/toggle_priority`, {
        method: 'PATCH',
        headers: jsonHeaders(),
      });
      if (!response.ok) {
        updateLocalProject(projectId, { isPriority: prev });
        toast.error('Failed to update priority');
      }
    } catch {
      updateLocalProject(projectId, { isPriority: prev });
      toast.error('Failed to update priority');
    }
  }

  function openRowActions(projectId: string, btnEl: HTMLElement) {
    closeAllInlineEdits();
    openFilter = null;
    if (rowActionsProjectId === projectId) {
      rowActionsProjectId = null;
      return;
    }
    rowActionsProjectId = projectId;
    rowActionsBtnEl = btnEl;
    tick().then(async () => {
      await positionRowActionsMenu();
      rowActionsFirstItemEl?.focus();
    });
  }

  async function positionRowActionsMenu() {
    if (!rowActionsBtnEl || !rowActionsPopoverEl) return;
    const result = await computePosition(rowActionsBtnEl, rowActionsPopoverEl, {
      strategy: 'fixed',
      placement: 'bottom-end',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    rowActionsStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  async function moveProjectToBacklog(projectId: string) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj) return;
    rowActionsProjectId = null;

    const previous = {
      schedulingState: proj.schedulingState,
      status: proj.status,
      monthKey: proj.monthKey ?? null,
      startDate: proj.startDate ?? null,
      endDate: proj.endDate ?? null,
      targetDate: proj.targetDate ?? null,
    };

    updateLocalProject(projectId, {
      schedulingState: 'backlog',
      status: 'planned',
      monthKey: null,
      startDate: null,
      endDate: null,
      targetDate: null,
    });

    try {
      const response = await fetch(`/projects/${projectId}/move_to_backlog`, {
        method: 'POST',
        headers: jsonHeaders(),
      });
      if (!response.ok) {
        updateLocalProject(projectId, previous);
        toast.error('Failed to move project to backlog');
        return;
      }
      const updated = await response.json();
      updateLocalProject(projectId, updated);
      toast('Moved to backlog');
    } catch {
      updateLocalProject(projectId, previous);
      toast.error('Failed to move project to backlog');
    }
  }

  async function scheduleProjectForWeek(projectId: string, weekKey: string, status: ProjectStatus) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj) return;

    const dates = scheduledDatesForWeek(weekKey);
    const previous = {
      schedulingState: proj.schedulingState,
      status: proj.status,
      monthKey: proj.monthKey ?? null,
      startDate: proj.startDate ?? null,
      endDate: proj.endDate ?? null,
      targetDate: proj.targetDate ?? null,
      originMonthKey: proj.originMonthKey ?? null,
    };

    updateLocalProject(projectId, {
      schedulingState: 'scheduled',
      status,
      ...dates,
      originMonthKey: proj.originMonthKey ?? dates.monthKey,
    });

    try {
      const response = await fetch(`/projects/${projectId}/move_to_week`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ week_key: weekKey, status }),
      });
      if (!response.ok) {
        updateLocalProject(projectId, previous);
        toast.error('Failed to schedule project');
        return;
      }
      const updated = await response.json();
      updateLocalProject(projectId, updated);
      toast('Scheduled for this week');
    } catch {
      updateLocalProject(projectId, previous);
      toast.error('Failed to schedule project');
    }
  }

  async function moveProjectToLane(projectId: string, targetLane: ProjectLane) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj) return;

    const move = resolveLaneMove(proj, targetLane, getCurrentWeekKey());
    if (move.type === 'none') return;
    if (move.type === 'backlog') {
      await moveProjectToBacklog(projectId);
      return;
    }
    if (move.type === 'status') {
      await setInlineStatus(projectId, move.status);
      return;
    }
    await scheduleProjectForWeek(projectId, move.weekKey, move.status);
  }

  function handleCompactLaneDragOver(event: DragEvent, lane: ProjectLane) {
    event.preventDefault();
    compactDropTarget = lane;
  }

  function handleCompactLaneDragLeave(event: DragEvent) {
    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as Node;
    if (!related || !current.contains(related)) compactDropTarget = null;
  }

  function handleCompactLaneDrop(event: DragEvent, lane: ProjectLane) {
    event.preventDefault();
    compactDropTarget = null;
    const projectId =
      event.dataTransfer?.getData('application/project') ||
      event.dataTransfer?.getData('text/plain');
    if (!projectId) return;
    void moveProjectToLane(projectId, lane);
    activeCompactLane = lane;
  }

  function openScheduleFromRowActions(project: Project) {
    rowActionsProjectId = null;
    openProjectScheduleModal(project);
  }

  function toggleProjectArchive(projectId: string) {
    const proj = localProjects.find((p) => p.id === projectId);
    if (!proj) return;
    rowActionsProjectId = null;

    const previous = { isArchived: !!proj.isArchived, archivedAt: proj.archivedAt ?? null };
    const nextArchived = !proj.isArchived;
    const optimistic = {
      isArchived: nextArchived,
      archivedAt: nextArchived ? new Date().toISOString() : null,
    };
    let committed = optimistic;
    const endpoint = previous.isArchived ? 'unarchive' : 'archive';
    const reverseEndpoint = nextArchived ? 'unarchive' : 'archive';

    undoableAction({
      message: nextArchived ? 'Project archived' : 'Project unarchived',
      duration: 7000,
      onOptimisticApply: () => updateLocalProject(projectId, optimistic),
      onCommit: async () => {
        const response = await fetch(`/projects/${projectId}/${endpoint}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error(await readApiErrorMessage(response));
        const updated = await response.json();
        committed = { isArchived: !!updated.isArchived, archivedAt: updated.archivedAt ?? null };
        updateLocalProject(projectId, committed);
      },
      onOptimisticUndo: () => updateLocalProject(projectId, previous),
      onUndo: async () => {
        const response = await fetch(`/projects/${projectId}/${reverseEndpoint}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error(await readApiErrorMessage(response));
        const reverted = await response.json();
        updateLocalProject(projectId, {
          isArchived: !!reverted.isArchived,
          archivedAt: reverted.archivedAt ?? null,
        });
      },
      onCommitError: () => updateLocalProject(projectId, previous),
      onUndoError: () => updateLocalProject(projectId, committed),
      commitErrorMessage: 'Failed to update archive state',
      undoErrorMessage: 'Failed to undo archive state',
    });
  }

  function deleteProject(projectId: string) {
    const projectToDelete = localProjects.find((p) => p.id === projectId);
    if (!projectToDelete) return;
    rowActionsProjectId = null;

    undoableSoftDelete({
      message: `Deleted "${projectToDelete.title}"`,
      onOptimisticRemove: () => {
        localProjects = localProjects.filter((p) => p.id !== projectId);
      },
      onOptimisticRestore: () => {
        if (localProjects.some((p) => p.id === projectId)) return;
        const originalIndex = projects.findIndex((p) => p.id === projectId);
        const next = [...localProjects];
        const insertAt = originalIndex < 0 ? next.length : Math.min(originalIndex, next.length);
        next.splice(insertAt, 0, projectToDelete);
        localProjects = next;
      },
      onDelete: async () => {
        const response = await fetch(`/projects/${projectId}`, {
          method: 'DELETE',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
      },
      onRestore: async () => {
        const response = await fetch(`/projects/${projectId}/undiscard`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
      },
    });
  }

  // Group-by state (persisted to localStorage)
  function loadPersistedGroupBy(): GroupBy {
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('projectIndex:groupBy');
        if (raw === 'month' || raw === 'status' || raw === 'none') return raw;
      } catch {
        /* use default */
      }
    }
    return 'none';
  }

  let groupBy = $state<GroupBy>(loadPersistedGroupBy());
  let collapsedGroups = $state<Set<string>>(new Set());

  function setGroupBy(value: GroupBy) {
    groupBy = value;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('projectIndex:groupBy', value);
    }
  }

  function toggleGroupCollapse(groupKey: string) {
    const next = new Set(collapsedGroups);
    if (next.has(groupKey)) {
      next.delete(groupKey);
    } else {
      next.add(groupKey);
    }
    collapsedGroups = next;
  }

  // Effective groupBy: disable month grouping on backlog tab (no dates)
  let effectiveGroupBy = $derived(
    groupBy === 'month' && activeTab === 'backlog' ? 'none' : groupBy,
  );

  let groupedProjects = $derived(groupProjects(sortedProjects, effectiveGroupBy));

  function handleNewProject() {
    if (activeTab === 'scheduled') {
      // Create as scheduled in the current month
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      openProjectModalForCreate({ schedulingState: 'scheduled', monthKey, status: 'planned' });
    } else {
      // For backlog, done, or all tabs — create in backlog and switch
      // to the backlog tab so the new project is immediately visible
      if (activeTab !== 'backlog') activeTab = 'backlog';
      openProjectModalForCreate({ schedulingState: 'backlog' });
    }
  }

  // Tooltip state
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="projects-page"
  class:lanes-page={effectiveViewMode === 'lanes'}
  onclick={handlePageClick}
  onkeydown={handleFilterKeydown}
>
  <!-- Header -->
  {#if $viewport.isMobile}
    <MobileTopBar title="Projects">
      {#snippet actions()}
        <button
          type="button"
          class="mtb-icon-btn m-proj-view-btn"
          aria-label="View: {VIEW_OPTIONS.find((o) => o.value === viewMode)?.label}"
          aria-haspopup="dialog"
          onclick={() => (viewSheetOpen = true)}
        >
          <span aria-hidden="true">{viewIcon}</span>
        </button>
        <button type="button" class="m-proj-new-btn" onclick={handleNewProject}>+ New</button>
      {/snippet}
    </MobileTopBar>
  {:else}
    <header class="page-header">
      <div class="header-left">
        <Link href="/" class="logo"
          ><img src={momentiumLogo} alt="Momentium" class="logo-icon" /><span class="logo-text"
            >Momentium</span
          ></Link
        >
        <NavDropdown activePage="projects" />
      </div>
      <div class="header-right">
        <button class="new-project-btn" onclick={handleNewProject}> + New Project </button>
        <ProfileDropdown />
      </div>
    </header>
  {/if}

  <div class="page-main">
    <div class="page-body" class:lanes-body={effectiveViewMode === 'lanes'}>
      <!-- Tabs + Filters -->
      <div class="toolbar">
        <div
          class="project-nav-row"
          use:scrollFade={{
            enabled: $viewport.isMobile,
            active: `${viewMode}:${activeTab}:${activeCompactLane}`,
            activeSelector: '.tab.active',
          }}
        >
          {#if !$viewport.isMobile}
            <div class="tablet-view-toggle" aria-label="Project view">
              <button
                type="button"
                class:active={viewMode === 'table'}
                aria-pressed={viewMode === 'table'}
                onclick={() => (viewMode = 'table')}>Table</button
              >
              <button
                type="button"
                class:active={viewMode === 'grid'}
                aria-pressed={viewMode === 'grid'}
                onclick={() => (viewMode = 'grid')}>Grid</button
              >
              <button
                type="button"
                class:active={viewMode === 'lanes'}
                aria-pressed={viewMode === 'lanes'}
                onclick={() => (viewMode = 'lanes')}>Lanes</button
              >
            </div>
          {/if}
          {#if effectiveViewMode !== 'lanes'}
            <div class="tabs">
              <button
                class="tab"
                class:active={activeTab === 'scheduled'}
                onclick={() => (activeTab = 'scheduled')}
              >
                <Icon name="calendar" size={12} />Scheduled
                <span class="tab-count">{activeProjects.length}</span>
              </button>
              <button
                class="tab"
                class:active={activeTab === 'backlog'}
                onclick={() => (activeTab = 'backlog')}
              >
                <Icon name="backlog" size={12} />Backlog
                <span class="tab-count">{backlogProjects.length}</span>
              </button>
              <button
                class="tab"
                class:active={activeTab === 'done'}
                onclick={() => (activeTab = 'done')}
              >
                <Icon name="check" size={12} />Done
                <span class="tab-count">{doneProjects.length}</span>
              </button>
              <button
                class="tab"
                class:active={activeTab === 'archived'}
                onclick={() => (activeTab = 'archived')}
              >
                <Icon name="archive" size={12} />Archived
                <span class="tab-count">{archivedProjects.length}</span>
              </button>
              <button
                class="tab"
                class:active={activeTab === 'all'}
                onclick={() => (activeTab = 'all')}
              >
                All <span class="tab-count">{filteredProjects.length}</span>
              </button>
            </div>
          {:else if compactLanes}
            <div class="tabs compact-lane-tabs" role="tablist" aria-label="Select project lane">
              {#each PROJECT_LANES as lane (lane.key)}
                <button
                  type="button"
                  class="tab"
                  role="tab"
                  class:active={activeCompactLane === lane.key}
                  class:drop-target={compactDropTarget === lane.key}
                  aria-selected={activeCompactLane === lane.key}
                  onclick={() => (activeCompactLane = lane.key)}
                  ondragover={(event) => handleCompactLaneDragOver(event, lane.key)}
                  ondragleave={handleCompactLaneDragLeave}
                  ondrop={(event) => handleCompactLaneDrop(event, lane.key)}
                  >{lane.icon} {lane.label}</button
                >
              {/each}
            </div>
          {/if}
        </div>

        <div class="filters" class:custom-sort={!isDefaultSort}>
          <input
            autocomplete="off"
            type="text"
            bind:value={searchQuery}
            placeholder="Search projects..."
            class="search-input"
          />
          <span class="sort-chip">
            <button
              type="button"
              class="sort-chip-toggle"
              onclick={() => ($viewport.isMobile ? (sortSheetOpen = true) : handleSort(sortColumn))}
              title={$viewport.isMobile
                ? undefined
                : `Sorted by ${sortColumnLabel}. Click to toggle direction.`}
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
              Sorted by {sortColumnLabel}
            </button>
            {#if !isDefaultSort}
              <button
                type="button"
                class="chip-dismiss"
                title="Reset to default sort"
                aria-label="Reset to default sort"
                onclick={resetSort}>✕</button
              >
            {/if}
          </span>
          <button
            class="mobile-filter-btn"
            onclick={() => (showMobileFilters = !showMobileFilters)}
          >
            Filter
            {#if hasActiveFilters}<span class="mobile-filter-badge"
                >{filterStatuses.size + filterCategories.size + (filterPriority ? 1 : 0)}</span
              >{/if}
            <span class="filter-chevron">{showMobileFilters ? '▲' : '▼'}</span>
          </button>
          <div class="filter-controls" class:mobile-show={showMobileFilters}>
            {#if effectiveViewMode !== 'lanes'}
              <div class="group-by-control">
                <span class="group-by-label">Group:</span>
                <button
                  class="group-by-btn"
                  class:active={groupBy === 'none'}
                  onclick={() => setGroupBy('none')}>None</button
                >
                <button
                  class="group-by-btn"
                  class:active={groupBy === 'month'}
                  onclick={() => setGroupBy('month')}>Month</button
                >
                <button
                  class="group-by-btn"
                  class:active={groupBy === 'status'}
                  onclick={() => setGroupBy('status')}>Status</button
                >
              </div>
            {/if}
            <div class="view-toggle">
              <button
                class="view-toggle-btn"
                class:active={viewMode === 'table'}
                onclick={() => (viewMode = 'table')}
                title="Table view">≡</button
              >
              <button
                class="view-toggle-btn"
                class:active={viewMode === 'grid'}
                onclick={() => (viewMode = 'grid')}
                title="Grid view">⊞</button
              >
              <button
                class="view-toggle-btn"
                class:active={viewMode === 'lanes'}
                onclick={() => (viewMode = 'lanes')}
                title="Lanes view">▥</button
              >
            </div>
            {#if effectiveViewMode === 'lanes'}
              <LaneColumnsOptions
                {collapsedLanes}
                onToggleLane={toggleLaneCollapse}
                onShowAll={showAllLanes}
              />
              <button
                type="button"
                class="filter-btn"
                class:filter-active={filterPriority}
                aria-pressed={filterPriority}
                title={filterPriority ? 'Show all projects' : 'Show starred projects only'}
                onclick={() => (filterPriority = !filterPriority)}
              >
                ⭐ Starred
              </button>
            {/if}
            <div class="filter-dropdown-wrapper">
              <button
                bind:this={categoryBtnEl}
                class="filter-btn"
                class:filter-active={filterCategories.size > 0}
                onclick={(e: MouseEvent) => {
                  e.stopPropagation();
                  toggleFilter('category');
                }}
              >
                {categoryBtnLabel}
                <span class="filter-chevron">{openFilter === 'category' ? '▲' : '▼'}</span>
              </button>
              {#if openFilter === 'category'}
                <div
                  bind:this={categoryPopoverEl}
                  class="filter-popover"
                  style={categoryPopoverStyle}
                >
                  {#each filters.categories as cat}
                    <label class="filter-option">
                      <input
                        type="checkbox"
                        checked={filterCategories.has(cat)}
                        onchange={() =>
                          (filterCategories = toggleFilterValue(filterCategories, cat))}
                      />
                      <span class="filter-option-label"
                        >{categoryIcons[cat as ProjectCategory]} {cat}</span
                      >
                    </label>
                  {/each}
                  {#if filterCategories.size > 0}
                    <div class="filter-popover-divider"></div>
                    <button class="filter-clear-btn" onclick={() => (filterCategories = new Set())}
                      >Clear all</button
                    >
                  {/if}
                </div>
              {/if}
            </div>
            {#if effectiveViewMode !== 'lanes'}
              <div class="filter-dropdown-wrapper">
                <button
                  bind:this={statusBtnEl}
                  class="filter-btn"
                  class:filter-active={filterStatuses.size > 0}
                  onclick={(e: MouseEvent) => {
                    e.stopPropagation();
                    toggleFilter('status');
                  }}
                >
                  {statusBtnLabel}
                  <span class="filter-chevron">{openFilter === 'status' ? '▲' : '▼'}</span>
                </button>
                {#if openFilter === 'status'}
                  <div
                    bind:this={statusPopoverEl}
                    class="filter-popover"
                    style={statusPopoverStyle}
                  >
                    {#each filters.statuses as st}
                      <label class="filter-option">
                        <input
                          type="checkbox"
                          checked={filterStatuses.has(st)}
                          onchange={() => (filterStatuses = toggleFilterValue(filterStatuses, st))}
                        />
                        <span class="filter-option-label">{statusIcons[st]} {st}</span>
                      </label>
                    {/each}
                    {#if filterStatuses.size > 0}
                      <div class="filter-popover-divider"></div>
                      <button class="filter-clear-btn" onclick={() => (filterStatuses = new Set())}
                        >Clear all</button
                      >
                    {/if}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
          <!-- /.filter-controls -->
        </div>
      </div>

      <!-- Active filter chips -->
      {#if hasActiveFilters}
        <div class="active-filter-chips">
          {#each [...filterCategories] as cat}
            <span class="filter-chip">
              {categoryIcons[cat as ProjectCategory]}
              {cat}
              <button
                type="button"
                class="chip-dismiss"
                onclick={() => (filterCategories = toggleFilterValue(filterCategories, cat))}
                >✕</button
              >
            </span>
          {/each}
          {#if effectiveViewMode === 'lanes' && filterPriority}
            <span class="filter-chip">
              ⭐ Starred
              <button
                type="button"
                class="chip-dismiss"
                title="Clear starred filter"
                onclick={() => (filterPriority = false)}>✕</button
              >
            </span>
          {/if}
          {#if effectiveViewMode !== 'lanes'}
            {#each [...filterStatuses] as st}
              <span class="filter-chip">
                {statusIcons[st]}
                {st}
                <button
                  type="button"
                  class="chip-dismiss"
                  onclick={() => (filterStatuses = toggleFilterValue(filterStatuses, st))}>✕</button
                >
              </span>
            {/each}
          {/if}
          {#if hasActiveFilters}
            <button class="clear-all-link" onclick={clearAllFilters}>Clear all</button>
          {/if}
        </div>
      {/if}

      <!-- Project List -->
      <div class="project-list" class:lanes-list={effectiveViewMode === 'lanes'}>
        {#if effectiveViewMode === 'lanes'}
          <ProjectLanesView
            projects={lanesProjects}
            {collapsedLanes}
            compact={compactLanes}
            compactActiveLane={activeCompactLane}
            showCompactSelector={false}
            onToggleLane={toggleLaneCollapse}
            onMoveProject={moveProjectToLane}
          />
        {:else if displayedProjects.length === 0}
          <div class="empty-state">
            <span class="empty-icon">📂</span>
            <span class="empty-text">No projects found</span>
          </div>
        {:else if effectiveViewMode === 'table'}
          {#if $viewport.isMobile}
            <div class="m-proj-list">
              {#each groupedProjects as group (group.key)}
                {#if effectiveGroupBy !== 'none'}
                  <button
                    type="button"
                    class="m-proj-group"
                    aria-expanded={!collapsedGroups.has(group.key)}
                    onclick={() => toggleGroupCollapse(group.key)}
                  >
                    <span class="group-chevron">{collapsedGroups.has(group.key) ? '▶' : '▼'}</span>
                    <span class="group-label">{group.label}</span>
                    <span class="group-count">({group.projects.length})</span>
                  </button>
                {/if}
                {#if effectiveGroupBy === 'none' || !collapsedGroups.has(group.key)}
                  <div
                    role="list"
                    aria-label={effectiveGroupBy === 'none' ? 'Projects' : group.label}
                  >
                    {#each group.projects as project (project.id)}
                      {@const counts = project.taskCounts}
                      {@const pct =
                        counts && counts.total > 0
                          ? Math.round((counts.done / counts.total) * 100)
                          : 0}
                      <div class="m-proj-row" role="listitem">
                        <button
                          type="button"
                          class="m-proj-star"
                          class:active={project.isPriority}
                          aria-pressed={project.isPriority}
                          aria-label={`Priority: ${project.title}`}
                          onclick={() => toggleInlinePriority(project.id)}
                        >
                          <span aria-hidden="true">{project.isPriority ? '★' : '☆'}</span>
                        </button>
                        <Link href="/projects/{project.id}" class="m-proj-link" draggable="false">
                          <span class="m-proj-title">{project.title}</span>
                          <span class="m-proj-meta">
                            <span class="m-proj-date">
                              {#if project.schedulingState === 'backlog'}
                                <span class="date-backlog">Backlog</span>
                              {:else}
                                {formatProjectDateShort(project)}
                              {/if}
                            </span>
                            <span class="m-proj-dot" aria-hidden="true">·</span>
                            {#if counts && counts.total > 0}
                              <span class="m-proj-tasks">{counts.done}/{counts.total} tasks</span>
                              <span class="m-proj-bar" aria-hidden="true"
                                ><span
                                  class="m-proj-bar-fill"
                                  class:complete={pct === 100}
                                  style="width: {pct}%"
                                ></span></span
                              >
                            {:else}
                              <span class="m-proj-tasks m-proj-none">No tasks</span>
                            {/if}
                          </span>
                        </Link>
                        <button
                          type="button"
                          class="status-pill status-{project.status} clickable-pill m-proj-status"
                          aria-label={`Status: ${project.status}. Change status of ${project.title}`}
                          onclick={(e: MouseEvent) => {
                            e.stopPropagation();
                            openInlineStatusPicker(project.id, e.currentTarget as HTMLElement);
                          }}
                        >
                          {statusIcons[project.status]}
                          {project.status}
                        </button>
                      </div>
                    {/each}
                  </div>
                {/if}
              {/each}
            </div>
          {:else}
            <table class="projects-table">
              <thead>
                <tr>
                  <th
                    class="th-title sortable-th"
                    class:sort-active={sortColumn === 'title'}
                    aria-sort={ariaSortFor('title')}
                    onclick={() => handleSort('title')}
                  >
                    Project
                    {#if sortColumn === 'title'}<span class="sort-chevron"
                        >{sortDirection === 'asc' ? '▲' : '▼'}</span
                      >{/if}
                  </th>
                  <th
                    class="th-status sortable-th"
                    class:sort-active={sortColumn === 'status'}
                    aria-sort={ariaSortFor('status')}
                    onclick={() => handleSort('status')}
                  >
                    Status
                    {#if sortColumn === 'status'}<span class="sort-chevron"
                        >{sortDirection === 'asc' ? '▲' : '▼'}</span
                      >{/if}
                  </th>
                  <th
                    class="th-category sortable-th"
                    class:sort-active={sortColumn === 'category'}
                    aria-sort={ariaSortFor('category')}
                    onclick={() => handleSort('category')}
                  >
                    Category
                    {#if sortColumn === 'category'}<span class="sort-chevron"
                        >{sortDirection === 'asc' ? '▲' : '▼'}</span
                      >{/if}
                  </th>
                  <th
                    class="th-date sortable-th"
                    class:sort-active={sortColumn === 'targetDate'}
                    aria-sort={ariaSortFor('targetDate')}
                    onclick={() => handleSort('targetDate')}
                  >
                    Date
                    {#if sortColumn === 'targetDate'}<span class="sort-chevron"
                        >{sortDirection === 'asc' ? '▲' : '▼'}</span
                      >{/if}
                  </th>
                  <th
                    class="th-tasks sortable-th"
                    class:sort-active={sortColumn === 'tasks'}
                    aria-sort={ariaSortFor('tasks')}
                    onclick={() => handleSort('tasks')}
                  >
                    Tasks
                    {#if sortColumn === 'tasks'}<span class="sort-chevron"
                        >{sortDirection === 'asc' ? '▲' : '▼'}</span
                      >{/if}
                  </th>
                  <th class="th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {#each groupedProjects as group (group.key)}
                  {#if effectiveGroupBy !== 'none'}
                    <tr class="group-header-row" onclick={() => toggleGroupCollapse(group.key)}>
                      <td colspan="6" class="group-header-cell">
                        <span class="group-chevron"
                          >{collapsedGroups.has(group.key) ? '▶' : '▼'}</span
                        >
                        <span class="group-label">{group.label}</span>
                        <span class="group-count">({group.projects.length})</span>
                      </td>
                    </tr>
                  {/if}
                  {#if effectiveGroupBy === 'none' || !collapsedGroups.has(group.key)}
                    {#each group.projects as project (project.id)}
                      <tr class="project-row">
                        <td class="td-title">
                          <div class="project-title-cell">
                            <button
                              type="button"
                              class="inline-priority-btn"
                              class:active={project.isPriority}
                              onclick={(e: MouseEvent) => {
                                e.stopPropagation();
                                toggleInlinePriority(project.id);
                              }}
                              title={project.isPriority ? 'Remove priority' : 'Set as priority'}
                            >
                              {project.isPriority ? '⭐' : '☆'}
                            </button>
                            {#if editingTitleProjectId === project.id}
                              <input
                                autocomplete="off"
                                bind:this={titleInputEl}
                                bind:value={editingTitleValue}
                                type="text"
                                class="inline-title-input"
                                onclick={(e: MouseEvent) => e.stopPropagation()}
                                onblur={() => saveInlineTitle(project.id)}
                                onkeydown={(e: KeyboardEvent) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveInlineTitle(project.id);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelEditTitle();
                                  }
                                }}
                              />
                            {:else}
                              <Link
                                href="/projects/{project.id}"
                                class="inline-title-text"
                                title="Go to project">{project.title}</Link
                              >
                              <button
                                type="button"
                                class="inline-edit-title-btn hover-reveal"
                                onclick={(e: MouseEvent) => {
                                  e.stopPropagation();
                                  startEditTitle(project.id, project.title);
                                }}
                                title="Edit title"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path
                                    d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25a1.75 1.75 0 01.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 00-.354 0L3.463 11.098a.25.25 0 00-.064.108l-.563 1.97 1.971-.564a.25.25 0 00.108-.064l8.61-8.61a.25.25 0 000-.354L12.427 2.487z"
                                  />
                                </svg>
                              </button>
                            {/if}
                            {#if project.prefix}
                              <span class="project-prefix">{project.prefix}</span>
                            {/if}
                          </div>
                        </td>
                        <td class="td-status">
                          <button
                            type="button"
                            class="status-pill status-{project.status} clickable-pill"
                            onclick={(e: MouseEvent) => {
                              e.stopPropagation();
                              openInlineStatusPicker(project.id, e.currentTarget as HTMLElement);
                            }}
                            title="Click to change status"
                          >
                            {statusIcons[project.status]}
                            {project.status}
                          </button>
                        </td>
                        <td class="td-category">
                          <button
                            type="button"
                            class="category-pill {categoryColors[project.category]} clickable-pill"
                            onclick={(e: MouseEvent) => {
                              e.stopPropagation();
                              openInlineCategoryPicker(project.id, e.currentTarget as HTMLElement);
                            }}
                            title="Click to change category"
                          >
                            {categoryIcons[project.category]}
                            {project.category}
                          </button>
                        </td>
                        <td class="td-date">
                          {#if activeTab === 'scheduled' || activeTab === 'backlog'}
                            <button
                              type="button"
                              class="date-cell-btn"
                              onclick={(e: MouseEvent) => {
                                e.stopPropagation();
                                openProjectScheduleModal(project);
                              }}
                              title={project.schedulingState === 'backlog'
                                ? 'Schedule project'
                                : 'Adjust schedule'}
                            >
                              {#if project.schedulingState === 'backlog'}
                                <span class="date-backlog">Backlog</span>
                              {:else}
                                <span class="date-full">{formatProjectDate(project)}</span>
                                <span class="date-short">{formatProjectDateShort(project)}</span>
                                {@const weekSpan = formatProjectWeekSpan(project)}
                                {#if weekSpan}
                                  <span class="date-weeks-badge">{weekSpan}</span>
                                {/if}
                              {/if}
                            </button>
                          {:else if project.schedulingState === 'backlog'}
                            <span class="date-backlog">Backlog</span>
                          {:else}
                            <span class="date-full">{formatProjectDate(project)}</span>
                            <span class="date-short">{formatProjectDateShort(project)}</span>
                            {@const weekSpan = formatProjectWeekSpan(project)}
                            {#if weekSpan}
                              <span class="date-weeks-badge">{weekSpan}</span>
                            {/if}
                          {/if}
                        </td>
                        <td class="td-tasks">
                          {#if project.taskCounts && project.taskCounts.total > 0}
                            {@const pct = Math.round(
                              (project.taskCounts.done / project.taskCounts.total) * 100,
                            )}
                            <div class="task-progress-cell">
                              <div class="mini-progress-track">
                                <div
                                  class="mini-progress-fill"
                                  class:complete={pct === 100}
                                  style="width: {pct}%"
                                ></div>
                              </div>
                              <span class="task-progress">
                                {project.taskCounts.done}/{project.taskCounts.total}
                              </span>
                            </div>
                          {:else}
                            <span class="no-tasks">—</span>
                          {/if}
                        </td>
                        <td class="td-actions">
                          <button
                            type="button"
                            class="row-actions-btn"
                            onclick={(e: MouseEvent) => {
                              e.stopPropagation();
                              openRowActions(project.id, e.currentTarget as HTMLElement);
                            }}
                            title="Project actions"
                            aria-label={`Actions for ${project.title}`}
                          >
                            ⋯
                          </button>
                        </td>
                      </tr>
                    {/each}
                  {/if}
                {/each}
              </tbody>
            </table>
          {/if}
        {:else}
          <!-- Grid View -->
          {#each groupedProjects as group (group.key)}
            {#if effectiveGroupBy !== 'none'}
              <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
              <div class="grid-group-header" onclick={() => toggleGroupCollapse(group.key)}>
                <span class="group-chevron">{collapsedGroups.has(group.key) ? '▶' : '▼'}</span>
                <span class="group-label">{group.label}</span>
                <span class="group-count">({group.projects.length})</span>
              </div>
            {/if}
            {#if effectiveGroupBy === 'none' || !collapsedGroups.has(group.key)}
              <div class="project-grid">
                {#each group.projects as project (project.id)}
                  {@const pct =
                    project.taskCounts && project.taskCounts.total > 0
                      ? Math.round((project.taskCounts.done / project.taskCounts.total) * 100)
                      : 0}
                  {@const isExpanded = expandedCards.has(project.id)}
                  {@const taskData = cardTaskData[project.id]}
                  {@const incompleteTasks = taskData
                    ? taskData.tasks
                        .filter((t) => t.status === 'todo')
                        .sort((a, b) => {
                          const po: Record<string, number> = {
                            high: 0,
                            medium: 1,
                            low: 2,
                            none: 3,
                          };
                          return (
                            (po[a.priority] ?? 3) - (po[b.priority] ?? 3) || a.position - b.position
                          );
                        })
                    : []}
                  {@const completedTasks = taskData
                    ? taskData.tasks.filter((t) => t.status !== 'todo')
                    : []}
                  <div class="grid-card status-border-{project.status}" class:expanded={isExpanded}>
                    <div class="grid-card-header">
                      <div class="grid-card-badges">
                        <span class="category-pill {categoryColors[project.category]}">
                          {categoryIcons[project.category]}
                          {project.category}
                        </span>
                        <span class="status-pill status-{project.status}">
                          {statusIcons[project.status]}
                          {project.status}
                        </span>
                      </div>
                      <div class="grid-card-title-row">
                        {#if project.isPriority}
                          <span class="priority-star">⭐</span>
                        {/if}
                        <Link href="/projects/{project.id}" class="grid-card-title">
                          {project.title}
                        </Link>
                      </div>
                      <div class="grid-card-meta">
                        {#if activeTab === 'scheduled' || activeTab === 'backlog'}
                          <button
                            type="button"
                            class="grid-card-date date-cell-btn"
                            onclick={(e: MouseEvent) => {
                              e.stopPropagation();
                              openProjectScheduleModal(project);
                            }}
                            title={project.schedulingState === 'backlog'
                              ? 'Schedule project'
                              : 'Adjust schedule'}
                          >
                            📅 {#if project.schedulingState === 'backlog'}Backlog{:else}{formatProjectDate(
                                project,
                              )}{/if}
                            {#if project.schedulingState !== 'backlog' && formatProjectWeekSpan(project)}
                              <span class="date-weeks-badge">{formatProjectWeekSpan(project)}</span>
                            {/if}
                          </button>
                        {:else}
                          <span class="grid-card-date">
                            📅 {#if project.schedulingState === 'backlog'}Backlog{:else}{formatProjectDate(
                                project,
                              )}{/if}
                            {#if project.schedulingState !== 'backlog' && formatProjectWeekSpan(project)}
                              <span class="date-weeks-badge">{formatProjectWeekSpan(project)}</span>
                            {/if}
                          </span>
                        {/if}
                      </div>
                      {#if project.taskCounts && project.taskCounts.total > 0}
                        <div class="grid-card-progress">
                          <div class="grid-progress-track">
                            <div
                              class="grid-progress-fill"
                              class:complete={pct === 100}
                              style="--progress: {pct}%"
                            ></div>
                          </div>
                          <span class="grid-progress-label"
                            >{project.taskCounts.done}/{project.taskCounts.total}</span
                          >
                        </div>
                      {/if}
                      <button
                        type="button"
                        class="grid-card-expand-btn"
                        onclick={(e) => {
                          e.stopPropagation();
                          toggleCardExpand(project.id);
                        }}
                      >
                        <span class="expand-chevron" class:open={isExpanded}>▶</span>
                        {#if isExpanded}Hide tasks{:else}Show tasks{/if}
                        {#if project.taskCounts}({project.taskCounts.total}){/if}
                      </button>
                    </div>
                    {#if isExpanded}
                      <div class="grid-card-tasks">
                        {#if taskData?.loading}
                          <div class="grid-tasks-loading">Loading tasks...</div>
                        {:else if !taskData || taskData.tasks.length === 0}
                          <div class="grid-tasks-empty">No tasks yet</div>
                        {:else}
                          {#each incompleteTasks as task (task.id)}
                            <label class="grid-task-item">
                              <input
                                type="checkbox"
                                checked={false}
                                onchange={() => toggleCardTask(project.id, task.id)}
                              />
                              <span class="grid-task-title">{task.title}</span>
                            </label>
                          {/each}
                          {#if incompleteTasks.length === 0 && completedTasks.length > 0}
                            <div class="grid-tasks-all-done">All tasks completed! 🎉</div>
                          {/if}
                          {#if completedTasks.length > 0}
                            <div class="grid-completed-section">
                              <div class="grid-completed-header">
                                Completed ({completedTasks.length})
                              </div>
                              {#each completedTasks as task (task.id)}
                                <label class="grid-task-item completed">
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    onchange={() => toggleCardTask(project.id, task.id)}
                                  />
                                  <span class="grid-task-title">{task.title}</span>
                                </label>
                              {/each}
                            </div>
                          {/if}
                        {/if}
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    </div>
    {#if effectiveViewMode === 'lanes'}
      <ProjectRail allProjects={localProjects} floating={compactLanes} />
    {/if}
  </div>
</div>

{#if $viewport.isMobile}
  <OptionSheet
    open={viewSheetOpen}
    onClose={() => (viewSheetOpen = false)}
    title="View"
    options={VIEW_OPTIONS}
    selected={[viewMode]}
    onChange={([value]) => {
      if (value) viewMode = value as ViewMode;
    }}
  />
  <OptionSheet
    open={sortSheetOpen}
    onClose={() => (sortSheetOpen = false)}
    title="Sort by"
    options={sortSheetOptions}
    selected={[sortColumn]}
    onChange={([value]) => {
      if (value) handleSort(value as SortColumn);
    }}
  />
{/if}

<!-- Inline status picker popover -->
{#if inlineStatusPickerProjectId !== null}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="inline-picker-popover"
    style={inlineStatusPickerStyle}
    bind:this={inlineStatusPickerPopoverEl}
    onmousedown={(e: MouseEvent) => e.stopPropagation()}
  >
    <div class="inline-picker-label">Status</div>
    {#each statusOptions as opt}
      {@const currentProject = localProjects.find((p) => p.id === inlineStatusPickerProjectId)}
      <button
        type="button"
        class="inline-picker-option"
        class:active={currentProject?.status === opt.value}
        onclick={() => setInlineStatus(inlineStatusPickerProjectId!, opt.value)}
      >
        <span class="inline-picker-icon">{opt.icon}</span>
        <span class="inline-picker-text">{opt.label}</span>
        {#if currentProject?.status === opt.value}
          <span class="inline-picker-check">✓</span>
        {/if}
      </button>
    {/each}
  </div>
{/if}

<!-- Inline category picker popover -->
{#if inlineCategoryPickerProjectId !== null}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="inline-picker-popover"
    style={inlineCategoryPickerStyle}
    bind:this={inlineCategoryPickerPopoverEl}
    onmousedown={(e: MouseEvent) => e.stopPropagation()}
  >
    <div class="inline-picker-label">Category</div>
    {#each categoryOptions as opt}
      {@const currentProject = localProjects.find((p) => p.id === inlineCategoryPickerProjectId)}
      <button
        type="button"
        class="inline-picker-option"
        class:active={currentProject?.category === opt.value}
        onclick={() => setInlineCategory(inlineCategoryPickerProjectId!, opt.value)}
      >
        <span class="inline-picker-icon">{opt.icon}</span>
        <span class="inline-picker-text">{opt.label}</span>
        {#if currentProject?.category === opt.value}
          <span class="inline-picker-check">✓</span>
        {/if}
      </button>
    {/each}
  </div>
{/if}

<!-- Row actions menu -->
{#if rowActionsProjectId !== null}
  {@const rowProject = localProjects.find((p) => p.id === rowActionsProjectId)}
  {#if rowProject}
    <div
      class="row-actions-popover"
      style={rowActionsStyle}
      bind:this={rowActionsPopoverEl}
      role="menu"
      aria-label="Project actions"
      tabindex="-1"
      onmousedown={(e: MouseEvent) => e.stopPropagation()}
      onkeydown={(e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          rowActionsProjectId = null;
          rowActionsBtnEl?.focus();
        }
      }}
    >
      {#if activeTab === 'scheduled'}
        <button
          bind:this={rowActionsFirstItemEl}
          type="button"
          class="row-actions-item"
          role="menuitem"
          onclick={() => moveProjectToBacklog(rowProject.id)}
        >
          <Icon name="backlog" />Move to backlog
        </button>
      {:else if activeTab === 'backlog'}
        <button
          bind:this={rowActionsFirstItemEl}
          type="button"
          class="row-actions-item"
          role="menuitem"
          onclick={() => openScheduleFromRowActions(rowProject)}
        >
          <Icon name="calendar" />Set schedule
        </button>
      {:else}
        <button
          bind:this={rowActionsFirstItemEl}
          type="button"
          class="row-actions-item"
          role="menuitem"
          onclick={() => toggleProjectArchive(rowProject.id)}
        >
          {#if rowProject.isArchived}
            <Icon name="unarchive" />Unarchive project
          {:else}
            <Icon name="archive" />Archive project
          {/if}
        </button>
      {/if}
      {#if activeTab === 'scheduled' || activeTab === 'backlog'}
        <button
          type="button"
          class="row-actions-item"
          role="menuitem"
          onclick={() => toggleProjectArchive(rowProject.id)}
        >
          {#if rowProject.isArchived}
            <Icon name="unarchive" />Unarchive project
          {:else}
            <Icon name="archive" />Archive project
          {/if}
        </button>
      {/if}
      <div class="row-actions-divider"></div>
      <button
        type="button"
        class="row-actions-item danger"
        role="menuitem"
        onclick={() => deleteProject(rowProject.id)}
      >
        <Icon name="trash" />Delete project
      </button>
    </div>
  {/if}
{/if}

<ProjectModal />

<style>
  .projects-page {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #f9fafb;
  }

  .projects-page.lanes-page {
    height: 100%;
    overflow: hidden;
  }

  .page-main {
    display: flex;
    flex: 1;
    min-height: 0;
    min-width: 0;
    position: relative;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.logo) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #1f2937;
    text-decoration: none;
    flex-shrink: 0;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .new-project-btn {
    padding: 0.25rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .new-project-btn:hover {
    background: #2563eb;
  }

  .page-body {
    flex: 1;
    padding: 1rem 1.5rem;
    max-width: 72rem;
    margin: 0 auto;
    width: 100%;
    min-width: 0;
  }

  .page-body.lanes-body {
    max-width: none;
    margin: 0;
    padding: 0.5rem 0.625rem 0.5rem;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .project-list.lanes-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .tabs {
    display: flex;
    gap: 0.25rem;
  }

  .tab {
    display: inline-flex;
    align-items: center;
    gap: 0.3125rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .tab:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .tab.active {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }

  .tab-count {
    font-size: 0.625rem;
    color: #9ca3af;
    margin-left: 0.125rem;
  }

  .filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .search-input {
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    outline: none;
    width: 12rem;
    transition: border-color 0.15s;
  }

  .search-input:focus {
    border-color: #3b82f6;
  }

  .filter-dropdown-wrapper {
    position: relative;
  }

  .filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .filter-btn:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  .filter-btn.filter-active {
    color: #2563eb;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .filter-chevron {
    font-size: 0.5rem;
    color: #9ca3af;
  }

  .filter-popover {
    z-index: 50;
    min-width: 10rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 0.25rem 0;
  }

  .filter-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    cursor: pointer;
    transition: background 0.1s;
  }

  .filter-option:hover {
    background: #f3f4f6;
  }

  .filter-option input[type='checkbox'] {
    width: 0.8125rem;
    height: 0.8125rem;
    accent-color: #3b82f6;
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .filter-option-label {
    color: #374151;
  }

  .filter-popover-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.25rem 0;
  }

  .filter-clear-btn {
    display: block;
    width: 100%;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    color: #6b7280;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
  }

  .filter-clear-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .active-filter-chips {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }

  .sort-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    transition: all 0.1s;
  }

  .sort-chip:hover {
    color: #2563eb;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .sort-chip-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    color: inherit;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.25rem;
  }

  .chip-dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.5625rem;
    color: #93c5fd;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: all 0.1s;
  }

  .chip-dismiss:hover {
    color: #1d4ed8;
    background: rgba(37, 99, 235, 0.1);
  }

  .clear-all-link {
    font-size: 0.625rem;
    color: #6b7280;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem 0.25rem;
    transition: color 0.1s;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .clear-all-link:hover {
    color: #1f2937;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 3rem 0;
    color: #9ca3af;
    gap: 0.5rem;
  }

  .empty-icon {
    font-size: 2rem;
  }
  .empty-text {
    font-size: 0.875rem;
  }

  .projects-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    background: white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .projects-table th {
    padding: 0.5rem 0.75rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  .project-row {
    cursor: pointer;
    transition: background 0.1s;
  }

  .project-row:hover {
    background: #f9fafb;
  }

  .project-row td {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    border-bottom: 1px solid #f3f4f6;
    overflow: hidden;
  }

  .project-title-cell {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
  }

  .priority-star {
    font-size: 0.625rem;
  }

  .project-title {
    font-weight: 500;
    color: #1f2937;
  }

  .project-prefix {
    font-size: 0.625rem;
    color: #9ca3af;
    font-family: monospace;
    flex-shrink: 0;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    border-radius: 1rem;
    font-size: 0.625rem;
    font-weight: 500;
  }

  .status-planned {
    background: #eff6ff;
    color: #2563eb;
  }
  .status-active {
    background: #fffbeb;
    color: #d97706;
  }
  .status-almost {
    background: #fff7ed;
    color: #ea580c;
  }
  .status-done {
    background: #f0fdf4;
    color: #16a34a;
  }

  .category-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
  }

  .td-title {
    min-width: 0;
  }

  .td-status,
  .td-category,
  .td-tasks,
  .td-date,
  .td-actions {
    white-space: nowrap;
  }

  .td-date {
    color: #6b7280;
    font-size: 0.6875rem;
  }

  .date-cell-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    max-width: 100%;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    padding: 0.25rem 0.375rem;
    margin: -0.25rem -0.375rem;
    transition:
      background 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }

  .td-date .date-cell-btn {
    width: 100%;
  }

  .date-cell-btn:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .date-backlog {
    font-style: italic;
    color: #9ca3af;
  }

  .date-weeks-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 0.375rem;
    padding: 0.0625rem 0.375rem;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1.2;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    white-space: nowrap;
    vertical-align: middle;
  }

  .task-progress-cell {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .mini-progress-track {
    width: 4rem;
    height: 3px;
    background: #e5e7eb;
    border-radius: 1.5px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .mini-progress-fill {
    height: 100%;
    background: #3b82f6;
    border-radius: 1.5px;
    transition: width 0.3s ease;
    min-width: 0;
  }

  .mini-progress-fill.complete {
    background: #22c55e;
  }

  .task-progress {
    font-size: 0.6875rem;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .no-tasks {
    color: #d1d5db;
  }

  .th-title {
    width: auto;
  }
  .th-status {
    width: 7.5rem;
  }
  .th-category {
    width: 7.5rem;
  }
  .th-date {
    width: 13.5rem;
  }
  .th-tasks {
    width: 6.5rem;
  }
  .th-actions {
    width: 5.5rem;
    text-align: center;
  }

  .sortable-th {
    cursor: pointer;
    user-select: none;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .sortable-th:hover {
    background: #f3f4f6;
    color: #374151;
  }

  .sortable-th.sort-active {
    color: #2563eb;
    background: #eff6ff;
  }

  .sort-chevron {
    font-size: 0.625rem;
    margin-left: 0.25rem;
    color: #9ca3af;
    vertical-align: middle;
    transition: color 0.15s;
  }

  .sort-active .sort-chevron {
    color: #2563eb;
  }

  .td-actions {
    text-align: center;
  }

  .row-actions-btn {
    width: 1.5rem;
    height: 1.5rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    line-height: 1;
    font-size: 1rem;
  }

  .row-actions-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }

  /* Group-by control */
  .group-by-control {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .group-by-label {
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    padding: 0 0.25rem 0 0.375rem;
  }

  .group-by-btn {
    padding: 0.1875rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .group-by-btn:hover:not(.active) {
    color: #374151;
    background: rgba(255, 255, 255, 0.5);
  }

  .group-by-btn.active {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Group section headers */
  .group-header-row {
    cursor: pointer;
    user-select: none;
  }

  .group-header-row:hover .group-header-cell {
    background: #f3f4f6;
  }

  .group-header-cell {
    padding: 0.5rem 0.75rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    transition: background 0.1s;
  }

  .group-chevron {
    font-size: 0.5rem;
    margin-right: 0.375rem;
    color: #9ca3af;
    display: inline-block;
    width: 0.625rem;
    text-align: center;
  }

  .group-label {
    color: #4b5563;
  }

  .group-count {
    color: #9ca3af;
    font-weight: 400;
    margin-left: 0.25rem;
  }

  /* View Toggle */
  .view-toggle {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .view-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1;
  }

  .view-toggle-btn:hover:not(.active) {
    color: #374151;
    background: rgba(255, 255, 255, 0.5);
  }

  .view-toggle-btn.active {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Grid View */
  .project-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .grid-group-header {
    padding: 0.5rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: pointer;
    user-select: none;
    margin-bottom: 0.5rem;
    background: #f9fafb;
    border-radius: 0.25rem;
    transition: background 0.1s;
  }

  .grid-group-header:hover {
    background: #f3f4f6;
  }

  .grid-card {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    border-left: 4px solid #e5e7eb;
    transition: box-shadow 0.15s;
    display: flex;
    flex-direction: column;
  }

  .grid-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .grid-card.expanded {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  /* Status-colored left borders */
  .grid-card.status-border-planned {
    border-left-color: #93c5fd;
  }
  .grid-card.status-border-active {
    border-left-color: #fcd34d;
  }
  .grid-card.status-border-almost {
    border-left-color: #fdba74;
  }
  .grid-card.status-border-done {
    border-left-color: #86efac;
  }

  .grid-card-header {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .grid-card-badges {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .grid-card-title-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .grid-card-title {
    font-weight: 600;
    font-size: 0.8125rem;
    color: #1f2937;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.3;
  }

  .grid-card-title:hover {
    color: #3b82f6;
    text-decoration: underline;
  }

  .grid-card-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .grid-card-date {
    font-size: 0.6875rem;
    color: #6b7280;
  }

  button.grid-card-date {
    width: fit-content;
  }

  .grid-card-progress {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .grid-progress-track {
    flex: 1;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
  }

  .grid-progress-fill {
    position: absolute;
    inset: 0;
    border-radius: 3px;
    background: repeating-linear-gradient(
      -45deg,
      #3b82f6,
      #3b82f6 2px,
      rgba(59, 130, 246, 0.4) 2px,
      rgba(59, 130, 246, 0.4) 5px
    );
    clip-path: polygon(0 0, var(--progress, 0%) 0, var(--progress, 0%) 100%, 0 100%);
    transition: clip-path 0.3s ease;
  }

  .grid-progress-fill.complete {
    background: repeating-linear-gradient(
      -45deg,
      #22c55e,
      #22c55e 2px,
      rgba(34, 197, 94, 0.4) 2px,
      rgba(34, 197, 94, 0.4) 5px
    );
    clip-path: none;
  }

  .grid-progress-label {
    font-size: 0.6875rem;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .grid-card-expand-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #9ca3af;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.15s;
  }

  .grid-card-expand-btn:hover {
    color: #3b82f6;
  }

  .expand-chevron {
    font-size: 0.5rem;
    transition: transform 0.15s ease;
    display: inline-block;
  }

  .expand-chevron.open {
    transform: rotate(90deg);
  }

  /* Grid card tasks */
  .grid-card-tasks {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.5rem 0.75rem;
    background: #fafafa;
    border-top: 1px solid #f3f4f6;
    max-height: 10rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .grid-tasks-loading,
  .grid-tasks-empty {
    font-size: 0.6875rem;
    color: #9ca3af;
    text-align: center;
    padding: 0.5rem 0;
  }

  .grid-tasks-all-done {
    font-size: 0.6875rem;
    color: #16a34a;
    text-align: center;
    padding: 0.25rem 0;
  }

  .grid-task-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.1875rem 0;
    cursor: pointer;
    min-width: 0;
  }

  .grid-task-item input[type='checkbox'] {
    width: 0.8125rem;
    height: 0.8125rem;
    accent-color: #3b82f6;
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .grid-task-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.6875rem;
    color: #374151;
    line-height: 1.3;
  }

  .grid-task-item.completed .grid-task-title {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .grid-completed-section {
    margin-top: 0.25rem;
    padding-top: 0.25rem;
    border-top: 1px dashed #e5e7eb;
  }

  .grid-completed-header {
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.125rem;
  }

  /* === Inline Editing Styles === */

  /* Priority toggle button in table row */
  .inline-priority-btn {
    font-size: 0.625rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem;
    border-radius: 0.125rem;
    transition: all 0.1s;
    flex-shrink: 0;
    opacity: 0.3;
    line-height: 1;
  }

  .inline-priority-btn:hover {
    opacity: 1;
    background: #fefce8;
  }

  .inline-priority-btn.active {
    opacity: 1;
  }

  /* Title text — click to edit */
  .inline-title-text {
    display: block;
    flex: 1;
    font-weight: 500;
    color: #1f2937;
    text-decoration: none;
    cursor: pointer;
    padding: 0.0625rem 0.1875rem;
    border-radius: 0.1875rem;
    transition:
      background 0.1s,
      color 0.1s;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .inline-title-text:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.06);
  }

  .inline-edit-title-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.1875rem;
    color: #9ca3af;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    opacity: 0;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .project-row:hover .inline-edit-title-btn {
    opacity: 1;
  }

  .inline-edit-title-btn:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.08);
  }

  /* Title borderless input */
  .inline-title-input {
    font-weight: 500;
    color: #1f2937;
    font-size: inherit;
    font-family: inherit;
    padding: 0.0625rem 0.1875rem;
    border: none;
    border-bottom: 2px solid #3b82f6;
    border-radius: 0;
    outline: none;
    background: transparent;
    flex: 1;
    min-width: 4rem;
  }

  /* Clickable pill buttons (status/category in table) */
  .clickable-pill {
    cursor: pointer;
    border: 1px solid transparent;
    font-family: inherit;
    transition: all 0.15s;
  }

  .clickable-pill:hover {
    filter: brightness(0.95);
    border-color: rgba(0, 0, 0, 0.1);
  }

  /* Inline picker popovers */
  .inline-picker-popover {
    z-index: 200;
    min-width: 10rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
  }

  .inline-picker-label {
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.375rem 0.5rem 0.25rem;
  }

  .inline-picker-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    color: #374151;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
    width: 100%;
    font-family: inherit;
  }

  .inline-picker-option:hover {
    background: #f3f4f6;
  }

  .row-actions-popover {
    z-index: 210;
    min-width: 11rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
  }

  .row-actions-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
    border-radius: 0.375rem;
    color: #374151;
    cursor: pointer;
  }

  .row-actions-item:hover {
    background: #f3f4f6;
  }

  .row-actions-item :global(.icon) {
    color: #6b7280;
  }

  .row-actions-item.danger :global(.icon) {
    color: inherit;
  }

  .row-actions-item.danger {
    color: #b91c1c;
  }

  .row-actions-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.25rem 0;
  }

  .confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 300;
    padding: 1rem;
  }

  .confirm-dialog {
    width: min(28rem, 100%);
    background: white;
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
    padding: 1rem;
  }

  .confirm-message {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
  }

  .confirm-subtext {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: #4b5563;
  }

  .confirm-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .confirm-cancel-btn,
  .confirm-ok-btn {
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    border-radius: 0.375rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
  }

  .confirm-ok-btn.danger {
    border-color: #fecaca;
    background: #fee2e2;
    color: #b91c1c;
  }

  .inline-picker-option.active {
    background: #eff6ff;
    color: #2563eb;
  }

  .inline-picker-icon {
    font-size: 0.8125rem;
    flex-shrink: 0;
  }

  .inline-picker-text {
    flex: 1;
    font-weight: 500;
  }

  .inline-picker-check {
    font-size: 0.625rem;
    color: #2563eb;
    font-weight: 700;
  }

  /* Date format switching */
  .date-short {
    display: none;
  }

  /* Mobile filter toggle — hidden on desktop */
  .mobile-filter-btn {
    display: none;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    font-family: inherit;
  }

  .mobile-filter-btn:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  .mobile-filter-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1rem;
    height: 1rem;
    padding: 0 0.25rem;
    font-size: 0.5625rem;
    font-weight: 700;
    color: white;
    background: #3b82f6;
    border-radius: 1rem;
  }

  /* Filter controls wrapper — always visible on desktop */
  .filter-controls {
    display: contents;
  }

  /* === Responsive: Tablet (768–1023px) === */
  @media (max-width: 1023px) {
    .th-category,
    .td-category {
      display: none;
    }

    .date-full {
      display: none;
    }

    .date-short {
      display: inline;
    }

    .projects-table th {
      padding: 0.375rem 0.5rem;
    }

    .project-row td {
      padding: 0.375rem 0.5rem;
    }

    .toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .filters {
      flex-wrap: wrap;
    }

    .th-title {
      width: auto;
    }

    .th-status {
      width: 7rem;
    }

    .th-date {
      width: 11.5rem;
    }

    .th-tasks {
      width: 5.5rem;
    }

    .th-actions {
      width: 5rem;
    }
  }

  /* === Responsive: Mobile (<768px) === */
  @media (min-width: 768px) and (max-width: 1024px) {
    /* Match the full-bleed lanes canvas at tablet widths. */
    .page-body {
      padding: 0.5rem 0.625rem;
    }
  }

  @media (max-width: 767px) {
    .page-header {
      padding: 0.375rem 0.75rem;
    }

    .nav-toggle {
      display: none;
    }

    .header-left {
      gap: 0.5rem;
    }

    .page-body {
      padding: 0.75rem;
    }

    .toolbar {
      flex-direction: column;
      align-items: stretch;
      gap: 0.375rem;
    }

    .tabs {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      white-space: nowrap;
      padding-bottom: 0.125rem;
    }

    .tabs::-webkit-scrollbar {
      display: none;
    }

    .tab {
      flex-shrink: 0;
      min-height: 2.75rem;
    }

    .filters {
      flex-wrap: wrap;
      gap: 0.375rem;
    }

    .search-input {
      width: 100%;
      flex: 1;
      min-width: 0;
      min-height: 2.75rem;
      font-size: 1rem;
    }

    /* With a non-default sort the chip grows a ✕; give the search its own row
       instead of squeezing it to a stub. */
    .filters.custom-sort .search-input {
      flex: 1 0 100%;
    }

    .mobile-filter-btn {
      display: flex;
      min-height: 2.75rem;
    }

    .filter-controls {
      display: none;
      flex-wrap: wrap;
      gap: 0.375rem;
      width: 100%;
    }

    .filter-controls.mobile-show {
      display: flex;
    }

    .view-toggle {
      display: none;
    }

    /* A compact table keeps its selected presentation without page-wide overflow. */
    .projects-table th,
    .project-row td {
      padding: 0.5rem;
    }

    .th-status {
      width: 5.5rem;
    }

    .th-date {
      width: 6.25rem;
    }

    .th-tasks,
    .td-tasks,
    .th-actions,
    .td-actions {
      display: none;
    }

    /* Grid: single column on mobile */
    .project-grid {
      grid-template-columns: 1fr;
    }

    /* Grid cards: ensure adequate touch targets */
    .grid-card-expand-btn {
      min-height: 2.75rem;
    }

    .grid-task-item {
      min-height: 2.75rem;
    }

    .grid-task-item input[type='checkbox'] {
      width: 1.125rem;
      height: 1.125rem;
    }

    /* Active filter chips */
    .active-filter-chips {
      margin-bottom: 0.375rem;
    }

    .sort-chip,
    .filter-chip {
      min-height: 2rem;
    }

    /* The sort chip opens the "Sort by" sheet on phones: full-size target. */
    .sort-chip {
      padding-block: 0;
      font-size: 0.75rem;
    }

    .sort-chip-toggle {
      min-height: 2.75rem;
      margin-block: -1px;
    }

    /* A real 44px reset button, not an expanded hit area that would overlap the
       "Sorted by …" toggle next to it. */
    .sort-chip .chip-dismiss {
      width: 2.75rem;
      height: 2.75rem;
      margin-block: -1px;
      font-size: 0.75rem;
    }

    .new-project-btn {
      padding: 0.375rem 0.625rem;
      min-height: 2.75rem;
    }
  }

  @media (max-width: 30rem) {
    .th-date,
    .td-date {
      display: none;
    }
  }

  .project-nav-row {
    display: contents;
  }

  .tablet-view-toggle {
    display: none;
  }

  @media (max-width: 1024px) {
    /* Keep the compact presentation and status navigation together on one scrollable row. */
    .toolbar .view-toggle {
      display: none;
    }

    .project-nav-row {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 0.375rem;
      width: 100%;
      overflow-x: auto;
      padding-bottom: 0.125rem;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }

    .project-nav-row::-webkit-scrollbar {
      display: none;
    }

    .tablet-view-toggle {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 0.125rem;
      padding: 0.125rem;
      background: #f3f4f6;
      border-radius: 0.25rem;
    }

    .tablet-view-toggle button {
      padding: 0.1875rem 0.5rem;
      border: 0;
      border-radius: 0.1875rem;
      background: transparent;
      color: #6b7280;
      font: inherit;
      font-size: 0.625rem;
      font-weight: 500;
    }

    .tablet-view-toggle button.active {
      background: white;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    .project-nav-row .tabs {
      flex: 0 0 auto;
      overflow: visible;
      padding-bottom: 0;
    }
  }

  @media (max-width: 1024px) {
    /* Overlay the rail so compact lanes stay full-width while cards remain openable. */
    .page-main :global(.project-rail.floating) {
      display: flex;
      position: absolute;
      z-index: 30;
      top: 0.5rem;
      right: 0.5rem;
      bottom: 0.5rem;
      left: auto;
      width: min(20rem, calc(100% - 1rem));
      max-height: none;
      border: 1px solid #e5e7eb;
      border-radius: 0.375rem;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
      overflow: hidden;
    }

    .compact-lane-tabs .tab.drop-target {
      box-shadow: 0 0 0 2px #3b82f6;
    }
  }

  /* Mobile "new" button in MobileTopBar */
  .m-proj-new-btn {
    position: relative;
    height: 32px;
    margin: 0 4px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    white-space: nowrap;
  }

  /* 44px hit area around the 32px pill */
  .m-proj-new-btn::after {
    content: '';
    position: absolute;
    inset: -6px -4px;
  }

  .m-proj-new-btn:active {
    background: #2563eb;
  }

  /* === Mobile rows, status-tab scroller (ma-p6zx) === */
  @media (max-width: 767px) {
    /* One scrolling tab row: snap to tabs, fade whichever edge has more to reveal. */
    .project-nav-row {
      scroll-snap-type: x proximity;
      scroll-padding-inline: 0.75rem;
      -webkit-mask-image: linear-gradient(
        to right,
        transparent 0,
        #000 var(--fade-start, 0px),
        #000 calc(100% - var(--fade-end, 0px)),
        transparent 100%
      );
      mask-image: linear-gradient(
        to right,
        transparent 0,
        #000 var(--fade-start, 0px),
        #000 calc(100% - var(--fade-end, 0px)),
        transparent 100%
      );
    }

    .project-nav-row:global([data-fade-start]) {
      --fade-start: 1.75rem;
    }

    .project-nav-row:global([data-fade-end]) {
      --fade-end: 2.5rem;
    }

    .project-nav-row .tab {
      scroll-snap-align: start;
    }
  }

  .m-proj-list {
    background: white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .m-proj-group {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 2.75rem;
    padding: 0 0.75rem;
    border: 0;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
    cursor: pointer;
  }

  /* Whole row is the tap target: the title link is stretched over it and the
     star / status buttons sit above the stretched link. */
  .m-proj-row {
    position: relative;
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr) auto;
    align-items: center;
    min-height: 4rem;
    padding-right: 0.75rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .m-proj-row:last-child {
    border-bottom: 0;
  }

  .m-proj-row:active {
    background: #f9fafb;
  }

  .m-proj-star {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: 0;
    background: none;
    color: #9ca3af;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .m-proj-star.active {
    color: #f59e0b;
  }

  .m-proj-row :global(.m-proj-link) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.125rem;
    min-width: 0;
    padding: 0.5rem 0.5rem 0.5rem 0;
    color: #1f2937;
    text-decoration: none;
    /* The link covers the whole row: no iOS link preview callout or native link drag
       when the row is long-pressed (plans/mobile-v2.md §3.5). */
    -webkit-touch-callout: none;
  }

  .m-proj-row :global(.m-proj-link::after) {
    content: '';
    position: absolute;
    inset: 0;
  }

  .m-proj-title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .m-proj-meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
  }

  .m-proj-date {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .m-proj-dot {
    color: #d1d5db;
    line-height: 1;
  }

  .m-proj-tasks {
    font-variant-numeric: tabular-nums;
  }

  .m-proj-none {
    color: #9ca3af;
  }

  .m-proj-bar {
    flex: 0 0 2.5rem;
    height: 3px;
    border-radius: 1.5px;
    background: #e5e7eb;
    overflow: hidden;
  }

  .m-proj-bar-fill {
    display: block;
    height: 100%;
    background: #3b82f6;
  }

  .m-proj-bar-fill.complete {
    background: #22c55e;
  }

  .m-proj-status {
    position: relative;
    z-index: 1;
    border: 0;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* 44px hit area around the ~24px pill */
  .m-proj-status::after {
    content: '';
    position: absolute;
    inset: -0.6875rem -0.375rem;
  }

  .m-proj-view-btn {
    font-size: 1.25rem;
  }
</style>
